import { DateRange, guid, isNullOrUndefined, LQ, UTC } from 'rewire-common';
import { createWatcher, WatcherType, replace, freeze } from 'rewire-core';
import { IError } from 'rewire-ui';
import {
  ITimecard,
  IEmployeeTimecardStatus,
  IJob,
  ITemplate,
  IEmployeeSummary,
  isMultiple,
  IGroup,
  IOccupation,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import { DailyRollupModelBase, IRolledUpTimecard } from './DailyRollupModel';
import timecardModel, { TimecardModelBase } from './TimecardModel';
import { EditTimecardModel, IDetailRow } from './edit/EditTimecardModel';
import { IAuditLogFilters } from '../audit/AuditLogListModel';
import auditLogDialogModel from '../audit/AuditLogDialogModel';
import authService from '../services/AuthService';
import Mutations from '../graphql/Mutations';
import { notifications } from '../ui/Notifications';

export abstract class DailyTimecardModelBase {
  watch: WatcherType<DailyRollupModelBase>;
  editTimecard: EditTimecardModel;

  constructor() {
    this.watch = createWatcher();
    this.editTimecard = new EditTimecardModel('timecardEditor', { viewOnlyFn: () => !this.canEditTimecard });
    this.editTimecard.addListener('edit', this.updateTimecards);

    setTimeout(() => {
      this.load = this.load.bind(this);
      this.watch(this.rollupModel, ['selectedRolledUpTimecard'], () => {
        // prevent load being called twice when selecting new employee as it will already be called from refreshModels.
        if (this.timecardModel.isLoading) return;
        requestAnimationFrame(this.load);
      });
    }, 0);
  }

  abstract get timecardModel(): TimecardModelBase;

  get rollupModel(): DailyRollupModelBase {
    return this.timecardModel.rollupModel;
  }

  get selectedEmployee(): IEmployeeSummary | undefined {
    return this.timecardModel.selectedEmployee;
  }

  get selectedRolledUpTimecard(): IRolledUpTimecard | undefined {
    return this.rollupModel.selectedRolledUpTimecard;
  }

  get canEditTimecard(): boolean {
    return this.timecardModel.canEditTimecard && (this.selectedRolledUpTimecard?.canEditTimecard ?? false);
  }

  get canTransitionTimecard(): boolean {
    return this.timecardModel.canEditTimecard && (this.selectedRolledUpTimecard?.canTransitionTimecard ?? false);
  }

  get canDeleteTimecard(): boolean {
    return (
      !this.timecardModel.viewOnly &&
      !!this.selectedRolledUpTimecard &&
      this.selectedRolledUpTimecard.batchStatus !== 'closed' &&
      this.selectedRolledUpTimecard.batchStatus !== 'exported'
    );
  }

  get canUndoReversal(): boolean {
    if (!authService.evalSecurityToken('edit_adjustments')) return false;
    return !this.timecardModel.viewOnly && !!this.selectedRolledUpTimecard && this.selectedRolledUpTimecard.isReversal;
  }

  get canAddComment(): boolean {
    return !this.timecardModel.viewOnly && !!this.earnedDate;
  }

  undoReversal = async (): Promise<void> => {
    if (!this.canUndoReversal) {
      throw new Error('Cannot undo reversal for this timecard');
    }
    const employeeId = this.selectedEmployee?.id;
    if (!employeeId) {
      throw new Error('Cannot undo reversal for this timecard, no employee selected');
    }
    const earnedDate = this.earnedDate;
    if (!earnedDate) {
      throw new Error('Cannot undo reversal for this timecard, no earned date available');
    }
    await DataMutations.undoReversals([{ employeeId, earnedDate }]);
    requestAnimationFrame(() => {
      this.timecardModel.load();
    });
  };

  get earnedDate() {
    return this.selectedRolledUpTimecard?.earnedDate;
  }

  setError(_id: string, _field: string, _message: IError) {}

  reset() {
    this.editTimecard.reset();
  }

  commit() {
    this.editTimecard.commit();
  }

  load() {
    const defaultEmptyTimecard = this.timecardDefault;
    if (!defaultEmptyTimecard) return;

    this.editTimecard.load(
      defaultEmptyTimecard,
      this.selectedRolledUpTimecard?.template,
      this.selectedRolledUpTimecard?.timecards ?? [],
    );
  }

  get timecardDefault(): ITimecard | undefined {
    const _employee = this.selectedEmployee;
    if (!_employee) return undefined;
    const earnedDate = this.earnedDate;
    if (!earnedDate) return undefined;
    const employee = { id: _employee.id, name: _employee.name };

    let rollupUsedForProperties = this.selectedRolledUpTimecard;
    let job: IJob | undefined = this.getPropertyFromRollup('job', rollupUsedForProperties);
    let group: IGroup | undefined = job?.group;
    let occupation: IOccupation | undefined = job?.occupation;
    if (!job) {
      rollupUsedForProperties = undefined;
      // find rollup with a job whose effective range contains the selected earned date.
      for (const rollup of this.rollupModel.getTimecards()) {
        const rollupJob = this.getPropertyFromRollup('job', rollup);
        if (rollupJob) {
          rollupUsedForProperties = rollup;
          group = rollupJob.group;
          occupation = rollupJob.occupation;
        }
        if (rollupJob?.effective.inRange(earnedDate)) {
          job = rollupJob;
          break;
        }
      }
    }

    if (!group) {
      group = this.getPropertyFromRollups('group', rollupUsedForProperties);
    }
    if (!occupation) {
      occupation = this.getPropertyFromRollups('occupation', rollupUsedForProperties);
    }

    const template: ITemplate | undefined = this.getPropertyFromRollups('template', rollupUsedForProperties);

    return {
      id: guid(),
      earnedDate,
      employee,
      job,
      template,
      reason: template?.template?.[0].reason,
      group: group,
      occupation: occupation,
      shift: 0,
    } as ITimecard;
  }

  loadAuditLogDialog(type: 'timecard' | 'employee_timecard') {
    if (!this.earnedDate) {
      return;
    }
    const filter = { types: [type], effective: new DateRange(this.earnedDate, this.earnedDate) } as IAuditLogFilters;
    const entityDataFilters = [];
    if (this.selectedEmployee?.id) {
      entityDataFilters.push({ field: 'employeeId', eq: this.selectedEmployee?.id });
    }
    auditLogDialogModel.load(filter, entityDataFilters);
  }

  getPropertyFromRollups(property: keyof ITimecard, rollupToUse?: IRolledUpTimecard) {
    let propertyValue = this.getPropertyFromRollup(property, rollupToUse);
    if (isNullOrUndefined(propertyValue)) {
      for (const rollup of this.rollupModel.getTimecards()) {
        propertyValue = this.getPropertyFromRollup(property, rollup);
        if (!isNullOrUndefined(propertyValue)) {
          break;
        }
      }
    }
    return propertyValue;
  }

  getPropertyFromRollup(property: keyof ITimecard | keyof IRolledUpTimecard, rollup?: IRolledUpTimecard): any {
    if (!rollup) return;
    return !isMultiple(rollup[property as keyof IRolledUpTimecard])
      ? rollup[property as keyof IRolledUpTimecard]
      : rollup.timecards[0]?.[property as keyof ITimecard];
  }

  executeTransition = async (event: string) => {
    if (!this.selectedRolledUpTimecard) return;
    const employeeId = this.selectedEmployee?.id;
    if (!employeeId) return;
    const effective = new DateRange(this.earnedDate, this.earnedDate);

    const newStatus: IEmployeeTimecardStatus[] | undefined = await DataMutations.transitionTimecards(
      [employeeId],
      effective,
      event,
    );
    if (newStatus?.length) this.selectedRolledUpTimecard?.setStatus(newStatus[0]);
  };

  hasData(): boolean {
    return true;
  }

  hasChanges(): boolean {
    return this.editTimecard.hasChanges;
  }

  get manualTimecard() {
    return this.rollupModel.selectedRolledUpTimecard?.manualTimecard ?? false;
  }

  set manualTimecard(value: boolean) {
    if (this.rollupModel.selectedRolledUpTimecard?.manualTimecard === value || !this.canSetManualTimecard()) return;
    const employeeId = this.selectedEmployee?.id;
    if (!employeeId || !this.earnedDate) {
      notifications.error('Cannot set manual timecard status, no employee selected.');
      return;
    }
    Mutations.setManualTimecard(employeeId, this.earnedDate, value).then((success: boolean) => {
      if (!success) {
        notifications.error('Failed to set manual timecard status.');
        return;
      }
      this.rollupModel.selectedRolledUpTimecard!.manualTimecard = value;
    });
  }

  canSetManualTimecard() {
    return this.canEditTimecard && !!this.rollupModel.selectedRolledUpTimecard;
  }

  updateTimecards = (_row?: IDetailRow, eDate?: UTC) => {
    const rolledUpTimecard = !isNullOrUndefined(eDate)
      ? LQ(this.rollupModel.getTimecards())
          .filter((rt) => rt.earnedDate.utc === eDate.utc)
          .first()
      : this.selectedRolledUpTimecard;
    if (!rolledUpTimecard) {
      return;
    }
    const earnedDate = rolledUpTimecard.earnedDate;
    const timecards: (ITimecard & { found?: boolean })[] | undefined = this.editTimecard.getTimecards();
    if (!timecards) return;

    // if (!this.timecardModel.isNotInBatch(earnedDate)) {
    //   throw new Error(
    //     'You cannot make changes; the timecard is locked. Check for this timecard in any closed or exported batches.',
    //   );
    // }

    freeze(() => {
      const model = this.timecardModel.timecards;
      for (let index = model.length - 1; index >= 0; index--) {
        const timecard = model[index];
        if (timecard.earnedDate.utc !== earnedDate.utc) continue;
        const entry = timecards.find((e) => e.id === timecard.id);
        if (entry) {
          replace(timecard, entry);
          entry.found = true;
          continue;
        }
        model.splice(index, 1);
      }
      model.push(...timecards.filter((t) => !t.found));
    });

    this.rollupModel.updateRollup(rolledUpTimecard.id);
  };
}

class DailyTimecardModel extends DailyTimecardModelBase {
  constructor() {
    super();
  }

  get timecardModel(): TimecardModelBase {
    return timecardModel;
  }
}

const dailyTimecardModel = new DailyTimecardModel();
export default dailyTimecardModel;
